import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { styled } from '@mui/system';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { UploadLogoIcon } from '../../../vectors/index';
import { darkBlue } from '@styles/colors';
import { useUploadCompaniesCsv } from '../hooks/useUploadCompaniesCsv';
import CsvUploadedCompaniesModal from './CsvUploadedCompaniesModal';
import { LoadingButton } from '@mui/lab';
import { PLANS } from '@utils/pricing';

interface UploadCsvModalProps {
  open: boolean;
  handleClose: () => void;
  templateId: string;
  billingType: number;
}

const Input = styled('input')({
  display: 'none',
});

const UploadButton = styled(Button)({
  backgroundColor: darkBlue,
  color: '#fff',
  padding: '10px 20px',
  textTransform: 'none',
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&:hover': {
    backgroundColor: '#303f9f',
  },
});

const UploadCsvModal: React.FC<UploadCsvModalProps> = ({
  open,
  handleClose,
  templateId,
  billingType,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [showCompaniesDataModal, setShowCompaniesDataModal] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<'1' | '2' | '4'>('4');
  const { uploadCompaiesCsv, isUploadCompaiesCsv, data } =
    useUploadCompaniesCsv({ setErrorText });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name;
      
      if (fileType === 'text/csv' || fileName.endsWith('.csv')) {
        setFile(selectedFile);
      } else {
        alert('Please upload a valid CSV file.');
      }
    }
  };


  const hanldlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedPlan(event.target.value as '1' | '2' | '4');
    }
  };

  const handleSubmit = () => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          setShowCompaniesDataModal(true);
          setErrorText('');
          // handleClose();
        }
      },
    };
    if (file) {
      uploadCompaiesCsv(
        {
          file: file,
          template_id: templateId,
        },
        options,
      );
    } else {
      alert('Please select a CSV file to upload.');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <UploadLogoIcon />
          <Typography variant="h4">Upload CSV File</Typography>
        </DialogTitle>

        <DialogContent dividers>
          {errorText && (
            <Alert sx={{ margin: 2 }} severity="error">
              {errorText}
            </Alert>
          )}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            mt={1}
          >
            <label htmlFor="upload-csv-file">
              <Input
                accept=".csv"
                id="upload-csv-file"
                type="file"
                onChange={handleFileChange}
              />
              {/* @ts-ignore */}
              <UploadButton variant="contained" component="span">
                <UploadFileIcon /> Upload CSV
              </UploadButton>
            </label>
            {file && (
              <Typography textAlign='center' m={2} variant="body2" color="textSecondary">
                Selected File: <strong>{file.name}</strong>
              </Typography>
            )}
          </Box>
          {billingType !== 0 && (
            <Box mt={2}>
              <Typography>Select Plan: </Typography>
              <RadioGroup value={selectedPlan} onChange={hanldlePlanChange} row>
                <FormControlLabel
                  value="4"
                  control={<Radio />}
                  label={PLANS.STARTER.NAME}
                />
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label={PLANS.SMARTER.NAME}
                />
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label={PLANS.PRO.NAME}
                />
              </RadioGroup>
            </Box>
          )}
          <Stack spacing={2} mb={2} mt={3}>
            <LoadingButton
              loading={isUploadCompaiesCsv}
              disabled={!file}
              onClick={handleSubmit}
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
            <Button onClick={handleClose} variant="outlined" color="secondary">
              Cancel
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      {showCompaniesDataModal && (
        <CsvUploadedCompaniesModal
          open={showCompaniesDataModal}
          handleClose={() => {
            setShowCompaniesDataModal(false);
            handleClose();
          }}
          companiesData={data}
          templateId={templateId}
          selectedPlan={selectedPlan}
          billingType={billingType}
        />
      )}
    </>
  );
};

export default UploadCsvModal;
